import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { scrollableTableSizing } from '../styles/scrollableTable';
import { FiPackage, FiSearch, FiRefreshCw, FiChevronDown, FiChevronUp, FiExternalLink, FiCopy, FiTruck, FiCreditCard, FiBox, FiGift, FiDollarSign } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useLanguage } from '../../context/LanguageContext';
import { useAuthContext } from '../../hooks/useAuthContext';
import { formatOrderReference } from '../../utils/orderReference';

// Get Supabase URL for direct function calls
const getSupabaseUrl = () => {
  return process.env.REACT_APP_SUPABASE_URL || 'https://lxprwdplyftigaemzaua.supabase.co';
};

const Container = styled.div`
  padding: 32px;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow-x: visible;
  min-width: 0;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
  overflow-x: auto;
  margin-bottom: 24px;
  -webkit-overflow-scrolling: touch;
`;

const Tab = styled.button`
  padding: 14px 28px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.active ? '#1f2937' : '#6b7280'};
  background: ${props => props.active ? 'linear-gradient(to top, rgba(94, 251, 169, 0.1), transparent)' : 'none'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#5efba9' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  position: relative;
  overflow: hidden;

  &:hover {
    color: #1f2937;
    background: linear-gradient(to top, rgba(94, 251, 169, 0.05), transparent);
  }
  
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 3px;
    background: #5efba9;
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }
  
  &:hover::before {
    width: ${props => props.active ? '0' : '100%'};
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 13px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 16px;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  flex: 1;
  min-width: 200px;
  max-width: 400px;
  
  &:focus-within {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1), 0 1px 3px rgba(0, 0, 0, 0.05);
  }
  
  svg {
    color: #6b7280;
    transition: color 0.2s ease;
    flex-shrink: 0;
  }
  
  &:focus-within svg {
    color: #5efba9;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
  }
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;
  width: 300px;
  color: #1f2937;
  flex: 1;
  min-width: 150px;
  
  &::placeholder {
    color: #9ca3af;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    background: #5efba9;
    border-color: #5efba9;
    color: #1f2937;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(94, 251, 169, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: visible;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.3s ease;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
    
    &:hover {
      background: #9ca3af;
    }
  }
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: separate;
  border-spacing: 0;
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #374151;
  background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: sticky;
  top: 0;
  z-index: 10;
  
  &:first-child {
    padding-left: 24px;
    border-top-left-radius: 8px;
  }
  
  &:last-child {
    padding-right: 24px;
    border-top-right-radius: 8px;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1f2937;
  border-bottom: 1px solid #f3f4f6;
  background: white;
  vertical-align: middle;
  
  &:first-child {
    padding-left: 24px;
  }
  
  &:last-child {
    padding-right: 24px;
  }
`;

const TableRow = styled.tr`
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb !important;
    transform: scale(1.001);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    
    ${TableCell} {
      background: #f9fafb;
    }
  }
  
  &:nth-child(even) {
    ${TableCell} {
      background: #fafbfc;
    }
  }

  &:last-child ${TableCell} {
    border-bottom: none;
  }
  
  /* Add subtle left border accent on hover */
  &:hover ${TableCell}:first-child {
    border-left: 3px solid #5efba9;
  }
`;

const SharedBasketRow = styled(TableRow)`
  background: #ede9fe;
  
  &:hover {
    background: #ddd6fe;
  }
  
  & > td {
    padding: 20px 16px;
    border-bottom: none;
  }
  
  /* First row - extra top padding */
  ${props => props.isFirst && `
    & > td {
      padding-top: 24px;
    }
  `}
  
  /* Last row - extra bottom padding */
  ${props => props.isLast && `
    & > td {
      padding-bottom: 24px;
      border-bottom: 1px solid #e5e7eb;
    }
  `}
  
  /* Separator between rows */
  ${props => !props.isLast && `
    & > td {
      border-bottom: 2px dashed #c4b5fd;
    }
  `}
`;

const BasketLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #5b21b6;
  background: white;
  padding: 4px 10px;
  border-radius: 20px;
  font-weight: 600;
  margin-top: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  
  background: ${props => {
    const step = props.step;
    if (step === 0) return 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
    if (step === 1) return 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)';
    if (step === 2) return 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)';
    if (step === 3) return 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)';
    if (step >= 4) return 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)';
    return 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)';
  }};
  
  color: ${props => {
    const step = props.step;
    if (step === 0) return '#991b1b';
    if (step === 1) return '#92400e';
    if (step === 2) return '#1e40af';
    if (step === 3) return '#3730a3';
    if (step >= 4) return '#065f46';
    return '#374151';
  }};
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: ${props => (props.step === 2 || props.step === 3) ? 'pulse 2s infinite' : 'none'};
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const PaymentStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  
  background: ${props => 
    props.status === 'paid' 
      ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' 
      : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
  };
  
  color: ${props => props.status === 'paid' ? '#065f46' : '#991b1b'};
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 32px;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const ExpandedContent = styled.div`
  padding: 24px;
  background: linear-gradient(to bottom, #fafbfc 0%, #ffffff 100%);
  border-top: 2px solid #f3f4f6;
  animation: slideDown 0.3s ease;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ExpandedSection = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid #f3f4f6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '';
    width: 3px;
    height: 16px;
    background: linear-gradient(to bottom, #5efba9, #4de89e);
    border-radius: 2px;
  }
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
    transform: translateX(4px);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #f3f4f6;
  transition: all 0.2s ease;
  
  ${ItemRow}:hover & {
    border-color: #5efba9;
    transform: scale(1.05);
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemName = styled.div`
  font-weight: 500;
  color: #1f2937;
`;

const ItemMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TrackLink = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TrackText = styled.span`
  color: #3b82f6;
  font-size: 13px;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CopyIcon = styled(FiCopy)`
  cursor: pointer;
  color: #6b7280;
  font-size: 14px;
  
  &:hover {
    color: #3b82f6;
  }
`;

const ItemsBadge = styled.span`
  font-size: 11px;
  color: #059669;
  font-weight: 600;
  background: #d1fae5;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
`;

const CreateShipmentButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #5efba9;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4ae398;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SendEmailButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #1d4ed8;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SendAllEmailButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  color: #1d4ed8;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 600;

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 32px;
  border-radius: 16px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
`;

const ModalText = styled.p`
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 24px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  ${props => props.primary ? `
    background: #5efba9;
    color: #1f2937;
    &:hover { background: #4ae398; }
  ` : `
    background: #e5e7eb;
    color: #6b7280;
    &:hover { background: #d1d5db; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 24px auto;
  padding: 12px 32px;
  background: #5efba9;
  border: 1px solid #5efba9;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4dd699;
    border-color: #4dd699;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-left: 8px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ToastStack = styled.div`
  position: fixed;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 2000;
  max-width: min(420px, calc(100vw - 32px));
`;

const Toast = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-left: 6px solid ${(props) => {
    if (props.$type === 'success') return '#5efba9';
    if (props.$type === 'error') return '#dc2626';
    return '#1f2937';
  }};
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  padding: 12px 14px;
  color: #1f2937;
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const ToastIcon = styled.div`
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => {
    if (props.$type === 'success') return 'rgba(94, 251, 169, 0.2)';
    if (props.$type === 'error') return 'rgba(220, 38, 38, 0.12)';
    return 'rgba(31, 41, 55, 0.08)';
  }};
  color: ${(props) => {
    if (props.$type === 'success') return '#065f46';
    if (props.$type === 'error') return '#991b1b';
    return '#1f2937';
  }};
  font-weight: 800;
  font-size: 14px;
`;

const ToastContent = styled.div`
  min-width: 0;
  flex: 1;
`;

const ToastTitle = styled.div`
  font-weight: 800;
  font-size: 13px;
  margin-bottom: 2px;
`;

const ToastMessage = styled.div`
  font-size: 13px;
  color: #374151;
  line-height: 1.35;
  word-break: break-word;
`;

const ToastClose = styled.button`
  flex: 0 0 auto;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  font-size: 18px;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 8px;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

const StatusTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  user-select: none;
`;

const StatusIcon = styled.div`
  cursor: default;
  pointer-events: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.completed 
    ? 'linear-gradient(135deg, #1f2937 0%, #374151 100%)' 
    : 'linear-gradient(135deg, #5efba9 0%, #4de89e 100%)'
  };
  color: white;
  border: 3px solid ${props => props.completed ? '#1f2937' : '#5efba9'};
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 8px ${props => props.completed 
    ? 'rgba(31, 41, 55, 0.3)' 
    : 'rgba(94, 251, 169, 0.4)'
  };
  transition: all 0.3s ease;
  position: relative;
  flex-shrink: 0;
  
  ${props => props.completed && `
    &::after {
      content: '✓';
      position: absolute;
      font-size: 14px;
      font-weight: bold;
    }
  `}
`;

const StatusLine = styled.div`
  height: 3px;
  width: 32px;
  background: ${props => props.completed 
    ? 'linear-gradient(90deg, #1f2937 0%, #5efba9 100%)' 
    : 'linear-gradient(90deg, #e5e7eb 0%, #d1d5db 100%)'
  };
  border-radius: 2px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  
  ${props => !props.completed && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(94, 251, 169, 0.5), transparent);
      animation: shimmer 2s infinite;
    }
    
    @keyframes shimmer {
      0% { left: -100%; }
      100% { left: 100%; }
    }
  `}
`;

// Map backend status to step index (supports both English and Arabic from WASL API)
const getStatusStepIndex = (shipmentStatusName) => {
  if (!shipmentStatusName) return 0;
  
  const status = shipmentStatusName.toLowerCase().trim();
  
  // Step 1: Shipping Request / طلب شحن
  if (
    status === 'shipping request' ||
    status === 'طلب شحن' ||
    status.includes('shipping request')
  ) return 1;
  
  // Step 2: Received in the store / تم الاستلام بالمخزن
  if (
    status === 'received in the store' ||
    status === 'تم الاستلام بالمخزن' ||
    status.includes('received') ||
    status.includes('الاستلام') ||
    status.includes('المخزن')
  ) return 2;
  
  // Step 3: Out To Deliver / جاري التوصيل
  if (
    status === 'out to deliver' ||
    status === 'جاري التوصيل' ||
    status.includes('out to deliver') ||
    status.includes('out for delivery') ||
    status.includes('جاري') ||
    status.includes('التوصيل')
  ) return 3;
  
  // Step 4: Delivered / تم التسليم
  if (
    status === 'delivered to consignee' ||
    status === 'تم التسليم' ||
    status === 'تم التسليم للمستلم' ||
    status === 'returning to shipper' ||
    status.includes('delivered') ||
    status.includes('تم التسليم')
  ) return 4;
  
  // Refund statuses
  if (status === 'refund' || status.includes('refund')) return 5;
  if (status === 'refunded' || status.includes('refunded')) return 6;
  
  return 0;
};

const getStatusLabel = (step) => {
  switch(step) {
    case 0: return 'Not Created';
    case 1: return 'Shipping Request';
    case 2: return 'Received in Store';
    case 3: return 'Out for Delivery';
    case 4: return 'Delivered';
    case 5:
    case 6: return 'Refunded';
    default: return 'Unknown';
  }
};

// Status step labels for tracker
const statusSteps = [
  "Shipping Request",
  "Received in the store",
  "Out for Delivery",
  "Delivered",
];

function SuperAdminStatus() {
  const { isArabic } = useLanguage();
  const { session } = useAuthContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const [brands, setBrands] = useState([]);
  const [shipmentModal, setShipmentModal] = useState({ open: false, brandId: null, orderId: null, brandName: '', country: null, forceOman: false });
  const [creatingShipment, setCreatingShipment] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(20);
  const [customFieldsMap, setCustomFieldsMap] = useState({}); // key_name -> {label, label_ar}
  const [addressModal, setAddressModal] = useState({ open: false, orderId: null });
  const [addressForm, setAddressForm] = useState({
    full_name: '',
    phone_number: '',
    email: '',
    country: 'Oman',
    governorate: '',
    city: '',
    area: '',
    delivery_notes: '',
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [emailModal, setEmailModal] = useState({
    open: false,
    orderItemId: null,
    statusName: '',
    trackingUrl: null,
    customerEmail: null,
    orderId: null,
  });
  const [sendingStatusEmail, setSendingStatusEmail] = useState(false);
  const [bulkEmailModalOpen, setBulkEmailModalOpen] = useState(false);
  const [sendingBulkEmails, setSendingBulkEmails] = useState(false);

  const showToast = ({ type = 'info', title = '', message = '' }) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((prev) => [...prev, { id, type, title, message }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3200);
  };

  useEffect(() => {
    fetchBrands();
    fetchOrders();
    fetchCountryOptions();
  }, []);

  // Reset displayed count when tab or search changes
  useEffect(() => {
    setDisplayedCount(20);
  }, [activeTab, searchTerm]);

  const fetchBrands = async () => {
    try {
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBrands(data || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const fetchCountryOptions = async () => {
    try {
      const { data, error } = await supabase
        .from('shipping_countries')
        .select('name')
        .order('name', { ascending: true });

      if (error) throw error;
      setCountryOptions((data || []).map((row) => row.name).filter(Boolean));
    } catch (err) {
      console.error('Error fetching shipping countries:', err);
      setCountryOptions([]);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          created_at,
          updated_at,
          profile_id,
          cart_id,
          currency,
          subtotal,
          shipping_fee,
          discount_total,
          tax_total,
          grand_total,
          status,
          payment_status,
          fulfillment_status,
          payment_method,
          payment_reference,
          shipping_information_id,
          shipping_snapshot,
          customer_note,
          admin_note,
          order_items(
            *,
            products(
              id,
              name,
              images,
              sku
            )
          )
        `)
        .eq('payment_status', 'paid')
        .order('created_at', { ascending: false });

      if (ordersError) {
        console.error('Orders query error:', ordersError);
        throw ordersError;
      }

      if (!ordersData) {
        setOrders([]);
        return;
      }

      const visibleOrdersData = (ordersData || []).filter((order) => {
        const status = String(order?.status || '').toLowerCase();
        return status !== 'canceled' && status !== 'cancelled';
      });

      // Get unique profile IDs to fetch emails using Postgres function
      const uniqueProfileIds = [...new Set(visibleOrdersData.map(order => order.profile_id).filter(Boolean))];
      console.log('📧 Fetching emails using get_user_email() function for profile IDs:', uniqueProfileIds);
      
      // Fetch customer emails using the Postgres function (batched concurrency for speed)
      const customerEmails = {};
      const EMAIL_BATCH_SIZE = 12;
      for (let i = 0; i < uniqueProfileIds.length; i += EMAIL_BATCH_SIZE) {
        const batch = uniqueProfileIds.slice(i, i + EMAIL_BATCH_SIZE);
        const results = await Promise.all(
          batch.map(async (profileId) => {
            try {
              const { data: email, error: rpcError } = await supabase.rpc('get_user_email', {
                user_id: profileId
              });
              if (rpcError) {
                console.error(`❌ RPC error for profile ${profileId}:`, rpcError.message);
                return null;
              }
              if (email) {
                return { profileId, email };
              }
            } catch (error) {
              console.error(`Error fetching email for profile ${profileId}:`, error.message);
            }
            return null;
          })
        );

        results.forEach((result) => {
          if (result) {
            customerEmails[result.profileId] = result.email;
          }
        });
      }
      
      console.log('📧 Total emails fetched:', Object.keys(customerEmails).length);

      // Group by order + brand, calculate totals
      // First, fetch all active brands to verify brand existence
      const { data: activeBrands, error: brandsError } = await supabase
        .from('brands')
        .select('id')
        .eq('active', true);

      const activeBrandIds = new Set((activeBrands || []).map(b => b.id));
      if (brandsError) {
        console.error('Error fetching active brands:', brandsError);
      }

      const transformedOrders = visibleOrdersData.flatMap(order => {
        if (!order.order_items || order.order_items.length === 0) return [];
        
        // Group items by brand_id (skip items without brand_id or with deleted brands)
        const grouped = order.order_items.reduce((acc, item) => {
          const brandId = item.brand_id;
          if (!brandId) {
            console.warn('Order item without brand_id:', item);
            return acc;
          }
          // Check if brand exists and is active
          if (!activeBrandIds.has(brandId)) {
            console.warn(`Order item with deleted/inactive brand_id ${brandId}:`, item);
            return acc;
          }
          if (!acc[brandId]) acc[brandId] = [];
          acc[brandId].push(item);
          return acc;
        }, {});

        const brandCount = Object.keys(grouped).length;
        
        // Skip orders with no valid brands
        if (brandCount === 0) {
          console.log(`Filtering out order ${order.id} - no valid active brands found`);
          return [];
        }
        
        // Debug: log orders with multiple brands
        if (brandCount > 1) {
          console.log(`Order ${order.id} has ${brandCount} brands:`, Object.keys(grouped));
        }

        // Create one order per brand_id with calculated totals
        return Object.entries(grouped).map(([brandId, items]) => {
          const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
          const totalAmount = items.reduce((sum, item) => sum + parseFloat(item.line_total || 0), 0);
          const firstItemStatus = items[0]?.shipment_status_name;
          const trackingUrl = items[0]?.tracking_url;
          const isRefunded = items.some(item => item.isRefunded);
          
          return {
            ...order,
            brandId,
            order_items: items,
            totalQuantity,
            totalAmount,
            shipmentStatus: firstItemStatus,
            statusStep: getStatusStepIndex(firstItemStatus),
            tracking_url: trackingUrl,
            isRefunded,
            customerEmail: customerEmails[order.profile_id] || order.shipping_snapshot?.email || null,
            // Track if this order has multiple brands (same basket)
            brandsInBasket: brandCount,
            basketId: order.id // Original order ID for grouping
          };
        });
      });

      // Count how many entries share each basketId
      const basketCounts = transformedOrders.reduce((acc, order) => {
        acc[order.basketId] = (acc[order.basketId] || 0) + 1;
        return acc;
      }, {});

      // Add basket info to each order
      transformedOrders.forEach(order => {
        order.sharedBasket = basketCounts[order.basketId] > 1;
        order.basketBrandCount = basketCounts[order.basketId];
      });

      // Sort orders so shared baskets appear together, then by date
      transformedOrders.sort((a, b) => {
        // First, group by basketId (order.id)
        if (a.basketId !== b.basketId) {
          // Sort by date descending
          return new Date(b.created_at) - new Date(a.created_at);
        }
        // Within same basket, sort by brand name
        return (a.brandId || '').localeCompare(b.brandId || '');
      });

      // Fetch custom fields for all products in orders
      const customFieldsMap = {};
      const allProductIds = new Set();
      transformedOrders.forEach(order => {
        (order.order_items || []).forEach(item => {
          if (item.products?.id) {
            allProductIds.add(item.products.id);
          }
        });
      });
      
      const productIds = Array.from(allProductIds);
      if (productIds.length > 0) {
        const { data: customFieldsData } = await supabase
          .from('product_custom_fields')
          .select('key_name, label, label_ar')
          .in('product_id', productIds);
        
        if (customFieldsData) {
          customFieldsData.forEach(field => {
            customFieldsMap[field.key_name] = {
              label: field.label,
              label_ar: field.label_ar
            };
          });
        }
      }
      
      setCustomFieldsMap(customFieldsMap);

      setOrders(transformedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (key) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const openShipmentModal = (brandId, orderId, brandName, country = null) => {
    const isInternational = country && String(country).toLowerCase() !== 'oman';
    setShipmentModal({ open: true, brandId, orderId, brandName, country, forceOman: !!isInternational });
  };

  const closeShipmentModal = () => {
    setShipmentModal({ open: false, brandId: null, orderId: null, brandName: '', country: null, forceOman: false });
  };

  const openEditAddressModal = async (orderId) => {
    try {
      const { data: orderRow, error } = await supabase
        .from('orders')
        .select('id, shipping_snapshot')
        .eq('id', orderId)
        .single();

      if (error || !orderRow) {
        showToast({ type: 'error', title: isArabic ? 'فشل' : 'Failed', message: isArabic ? 'تعذر تحميل عنوان الطلب' : 'Failed to load order address' });
        return;
      }

      const snap = (orderRow.shipping_snapshot && typeof orderRow.shipping_snapshot === 'object') ? orderRow.shipping_snapshot : {};
      setAddressForm({
        full_name: snap.full_name || '',
        phone_number: snap.phone_number || '',
        email: snap.email || '',
        country: snap.country || 'Oman',
        governorate: snap.governorate || '',
        city: snap.city || '',
        area: snap.area || '',
        delivery_notes: snap.delivery_notes || '',
      });
      setAddressModal({ open: true, orderId });
    } catch (err) {
      showToast({ type: 'error', title: isArabic ? 'خطأ' : 'Error', message: err?.message || String(err) });
    }
  };

  const closeEditAddressModal = () => {
    setAddressModal({ open: false, orderId: null });
    setSavingAddress(false);
  };

  const saveOrderAddress = async () => {
    if (!addressModal.orderId) return;
    try {
      setSavingAddress(true);

      const { data: orderRow, error: orderErr } = await supabase
        .from('orders')
        .select('id, shipping_snapshot, shipping_information_id')
        .eq('id', addressModal.orderId)
        .single();

      if (orderErr || !orderRow) {
        showToast({ type: 'error', title: isArabic ? 'فشل' : 'Failed', message: isArabic ? 'تعذر تحميل الطلب' : 'Failed to load order' });
        return;
      }

      const currentSnapshot = (orderRow.shipping_snapshot && typeof orderRow.shipping_snapshot === 'object')
        ? orderRow.shipping_snapshot
        : {};

      const cleaned = {
        full_name: addressForm.full_name?.trim() || null,
        phone_number: addressForm.phone_number?.trim() || null,
        email: addressForm.email?.trim() || null,
        country: addressForm.country?.trim() || 'Oman',
        governorate: addressForm.governorate?.trim() || null,
        city: addressForm.city?.trim() || null,
        area: addressForm.area?.trim() || null,
        delivery_notes: addressForm.delivery_notes?.trim() || null,
      };

      const mergedSnapshot = { ...currentSnapshot, ...cleaned };

      const { error: updateOrderErr } = await supabase
        .from('orders')
        .update({ shipping_snapshot: mergedSnapshot })
        .eq('id', addressModal.orderId);

      if (updateOrderErr) {
        showToast({ type: 'error', title: isArabic ? 'فشل' : 'Failed', message: (isArabic ? 'فشل حفظ العنوان: ' : 'Failed to save address: ') + updateOrderErr.message });
        return;
      }

      if (orderRow.shipping_information_id) {
        const { error: shipInfoErr } = await supabase
          .from('shipping_information')
          .update({
            full_name: mergedSnapshot.full_name,
            phone_number: mergedSnapshot.phone_number,
            email: mergedSnapshot.email,
            country: mergedSnapshot.country,
            governorate: mergedSnapshot.governorate,
            city: mergedSnapshot.city,
            area: mergedSnapshot.area,
            delivery_notes: mergedSnapshot.delivery_notes,
          })
          .eq('id', orderRow.shipping_information_id);

        if (shipInfoErr) {
          console.warn('Failed updating shipping_information:', shipInfoErr);
        }
      }

      await fetchOrders();
      closeEditAddressModal();
      showToast({ type: 'success', title: isArabic ? 'تم الحفظ' : 'Saved', message: isArabic ? 'تم حفظ العنوان' : 'Address saved' });
    } catch (err) {
      showToast({ type: 'error', title: isArabic ? 'خطأ' : 'Error', message: err?.message || String(err) });
    } finally {
      setSavingAddress(false);
    }
  };

  const createShipment = async () => {
    const { brandId, orderId } = shipmentModal;
    if (!brandId || !orderId) return;

    setCreatingShipment(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showToast({ type: 'error', title: isArabic ? 'غير مصرح' : 'Not authenticated', message: isArabic ? 'الرجاء تسجيل الدخول مرة أخرى' : 'Please login again.' });
        setCreatingShipment(false);
        return;
      }

      const supabaseUrl = getSupabaseUrl();
      const functionUrl = `${supabaseUrl}/functions/v1/create-shipment`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ brandId, orderId, forceOman: shipmentModal.forceOman }),
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        const text = await response.text();
        responseData = { error: text || `HTTP ${response.status}: ${response.statusText}` };
      }

      if (!response.ok) {
        const errorMessage = responseData?.error || responseData?.message || `Failed to create shipment (Status: ${response.status})`;
        showToast({ type: 'error', title: isArabic ? 'فشل إنشاء الشحنة' : 'Shipment failed', message: errorMessage });
        console.error('Error creating shipment:', responseData);
      } else {
        showToast({ type: 'success', title: isArabic ? 'تم إنشاء الشحنة' : 'Shipment created', message: responseData?.message || (isArabic ? 'تم إنشاء الشحنة بنجاح' : 'Shipment created successfully!') });
        fetchOrders(); // Refresh the list
      }
    } catch (error) {
      showToast({ type: 'error', title: isArabic ? 'خطأ' : 'Error', message: error.message });
      console.error('Error:', error);
    } finally {
      setCreatingShipment(false);
      closeShipmentModal();
    }
  };

  const openSendEmailModal = (order) => {
    const step = order.statusStep;
    if (step !== 3 && step !== 4) return;
    const firstItemId = order.order_items?.[0]?.id || null;
    if (!firstItemId) {
      showToast({ type: 'error', title: isArabic ? 'فشل' : 'Failed', message: isArabic ? 'تعذر تحديد عنصر الطلب للإيميل' : 'Could not determine order item for email' });
      return;
    }
    setEmailModal({
      open: true,
      orderItemId: firstItemId,
      statusName: step === 3 ? 'Out To Deliver' : 'Delivered To Consignee',
      trackingUrl: order.tracking_url || null,
      customerEmail: order.customerEmail || null,
      orderId: order.id || null,
    });
  };

  const closeSendEmailModal = () => {
    if (sendingStatusEmail) return;
    setEmailModal({
      open: false,
      orderItemId: null,
      statusName: '',
      trackingUrl: null,
      customerEmail: null,
      orderId: null,
    });
  };

  const confirmSendStatusEmail = async () => {
    if (!emailModal.orderItemId || !emailModal.statusName) return;
    try {
      setSendingStatusEmail(true);
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession) {
        showToast({ type: 'error', title: isArabic ? 'غير مصرح' : 'Not authenticated', message: isArabic ? 'الرجاء تسجيل الدخول مرة أخرى' : 'Please login again.' });
        return;
      }

      const functionUrl = `${getSupabaseUrl()}/functions/v1/send-shipment-email`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authSession.access_token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderItemId: emailModal.orderItemId,
          newStatus: emailModal.statusName,
          oldStatus: null,
          trackingUrl: emailModal.trackingUrl || null,
        }),
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (_e) {
        payload = {};
      }
      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || `HTTP ${response.status}`);
      }

      showToast({
        type: 'success',
        title: isArabic ? 'تم الإرسال' : 'Email sent',
        message: isArabic
          ? `تم إرسال إيميل الحالة${emailModal.customerEmail ? ` إلى ${emailModal.customerEmail}` : ''}`
          : `Shipment status email sent${emailModal.customerEmail ? ` to ${emailModal.customerEmail}` : ''}`
      });
      closeSendEmailModal();
    } catch (err) {
      showToast({ type: 'error', title: isArabic ? 'فشل الإرسال' : 'Send failed', message: err?.message || (isArabic ? 'تعذر إرسال الإيميل' : 'Could not send email') });
    } finally {
      setSendingStatusEmail(false);
    }
  };

  const getBulkEmailTargets = () => {
    return filteredOrders
      .map((order) => {
        if (order.statusStep !== 3 && order.statusStep !== 4) return null;
        const orderItemId = order.order_items?.[0]?.id;
        if (!orderItemId || !order.customerEmail) return null;
        return {
          orderId: order.id,
          orderItemId,
          statusName: order.statusStep === 3 ? 'Out To Deliver' : 'Delivered To Consignee',
          trackingUrl: order.tracking_url || null,
          customerEmail: order.customerEmail,
        };
      })
      .filter(Boolean);
  };

  const confirmSendBulkEmails = async () => {
    const targets = getBulkEmailTargets();
    if (targets.length === 0) {
      showToast({
        type: 'info',
        title: isArabic ? 'لا يوجد مستلمين' : 'No recipients',
        message: isArabic ? 'لا توجد طلبات مؤهلة للإرسال الجماعي' : 'No eligible orders for bulk email'
      });
      setBulkEmailModalOpen(false);
      return;
    }
    try {
      setSendingBulkEmails(true);
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession) {
        showToast({ type: 'error', title: isArabic ? 'غير مصرح' : 'Not authenticated', message: isArabic ? 'الرجاء تسجيل الدخول مرة أخرى' : 'Please login again.' });
        return;
      }

      const functionUrl = `${getSupabaseUrl()}/functions/v1/send-shipment-email`;
      const results = await Promise.allSettled(
        targets.map(async (target) => {
          const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authSession.access_token}`,
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              orderItemId: target.orderItemId,
              newStatus: target.statusName,
              oldStatus: null,
              trackingUrl: target.trackingUrl || null,
            }),
          });
          if (!response.ok) {
            let payload = {};
            try {
              payload = await response.json();
            } catch (_e) {
              payload = {};
            }
            throw new Error(payload?.error || payload?.message || `HTTP ${response.status}`);
          }
          return target;
        })
      );

      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failedCount = results.length - successCount;
      showToast({
        type: failedCount === 0 ? 'success' : 'info',
        title: failedCount === 0
          ? (isArabic ? 'تم الإرسال' : 'Emails sent')
          : (isArabic ? 'اكتمل الإرسال' : 'Bulk send completed'),
        message: isArabic
          ? `نجح: ${successCount}، فشل: ${failedCount}`
          : `Success: ${successCount}, Failed: ${failedCount}`
      });
      setBulkEmailModalOpen(false);
    } catch (err) {
      showToast({
        type: 'error',
        title: isArabic ? 'فشل الإرسال الجماعي' : 'Bulk send failed',
        message: err?.message || (isArabic ? 'تعذر إرسال الإيميلات' : 'Could not send bulk emails')
      });
    } finally {
      setSendingBulkEmails(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase().trim();
      const searchDigits = searchTerm.replace(/\D/g, ''); // Extract only digits from search term
      
      // If search term has digits, search by digits; otherwise search by text
      if (searchDigits.length > 0) {
      const brandName = brands.find(b => b.id === order.brandId)?.name || '';
      const phoneNumber = order.shipping_snapshot?.phone_number || '';
        const phoneDigits = phoneNumber.replace(/\D/g, ''); // Extract only digits from phone number
        const customerEmail = order.customerEmail || '';
        const orderIdStr = order.id.toString();
        const orderIdDigits = orderIdStr.replace(/\D/g, ''); // Extract only digits from order ID
        
        // Match if any field contains the search digits
        const matchesOrderId = orderIdDigits.includes(searchDigits);
        const matchesPhone = phoneDigits.includes(searchDigits);
        const matchesEmail = customerEmail.replace(/\D/g, '').includes(searchDigits);
        const matchesBrand = brandName.toLowerCase().includes(searchLower);
        const matchesName = order.shipping_snapshot?.full_name?.toLowerCase().includes(searchLower);
        
        if (!matchesOrderId && !matchesPhone && !matchesEmail && !matchesBrand && !matchesName) {
          return false;
        }
      } else {
        // Text-only search
        const brandName = brands.find(b => b.id === order.brandId)?.name || '';
        const phoneNumber = order.shipping_snapshot?.phone_number || '';
        const customerEmail = order.customerEmail || '';
        
      if (
        !order.id.toLowerCase().includes(searchLower) &&
        !brandName.toLowerCase().includes(searchLower) &&
        !order.shipping_snapshot?.full_name?.toLowerCase().includes(searchLower) &&
          !phoneNumber.toLowerCase().includes(searchLower) &&
          !customerEmail.toLowerCase().includes(searchLower)
      ) {
        return false;
        }
      }
    }

    // Apply tab filter
    if (activeTab === 'all') return true;
    if (activeTab === 'notCreated' && order.statusStep === 0) return true;
    if (activeTab === 'shippingRequest' && order.statusStep === 1) return true;
    if (activeTab === 'received' && order.statusStep === 2) return true;
    if (activeTab === 'out' && order.statusStep === 3) return true;
    if (activeTab === 'delivered' && order.statusStep === 4) return true;
    if (activeTab === 'refunded' && order.isRefunded) return true;

    return false;
  });

  // Slice to show only displayedCount items
  const displayedOrders = filteredOrders.slice(0, displayedCount);
  const hasMore = filteredOrders.length > displayedCount;

  const statusCounts = {
    all: orders.length,
    notCreated: orders.filter(t => t.statusStep === 0).length,
    shippingRequest: orders.filter(t => t.statusStep === 1).length,
    received: orders.filter(t => t.statusStep === 2).length,
    out: orders.filter(t => t.statusStep === 3).length,
    delivered: orders.filter(t => t.statusStep === 4).length,
    refunded: orders.filter(t => t.isRefunded).length,
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBrandName = (brandId) => {
    return brands.find(b => b.id === brandId)?.name || 'Unknown';
  };


  // Status Tracker Component (read-only for SuperAdmin - status comes from API)
  const StatusTracker = ({ progress, isRefunded, orderItemId, allOrderItemIds = null }) => {
    return (
      <StatusTrack
        role="presentation"
        aria-label={`Shipment status tracker, progress ${progress}`}
      >
        {[...Array(4)].map((_, idx) => {
          return (
            <React.Fragment key={idx}>
              <StatusIcon
                completed={progress > idx}
                title={statusSteps[idx]}
              >
                {idx === 0 && <FiCreditCard />}
                {idx === 1 && <FiBox />}
                {idx === 2 && <FiTruck />}
                {idx === 3 && <FiGift />}
              </StatusIcon>
              {idx < 3 && <StatusLine completed={progress > idx} />}
            </React.Fragment>
          );
        })}

        <StatusLine completed={progress > 3} />
        <StatusIcon
          completed={isRefunded}
          title={isRefunded ? 'Refunded' : 'Not Refunded'}
        >
          <FiDollarSign />
        </StatusIcon>
      </StatusTrack>
    );
  };

  if (loading) {
    return (
      <Container>
        <EmptyState>Loading orders...</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <ToastStack>
        {toasts.map((t) => (
          <Toast key={t.id} $type={t.type}>
            <ToastIcon $type={t.type}>
              {t.type === 'success' ? '✓' : t.type === 'error' ? '!' : 'i'}
            </ToastIcon>
            <ToastContent>
              {t.title ? <ToastTitle>{t.title}</ToastTitle> : null}
              {t.message ? <ToastMessage>{t.message}</ToastMessage> : null}
            </ToastContent>
            <ToastClose onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))} aria-label="Close">
              ×
            </ToastClose>
          </Toast>
        ))}
      </ToastStack>
      <FilterTabs>
        <Tab active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
          {isArabic ? 'الكل' : 'All'} ({statusCounts.all})
        </Tab>
        <Tab active={activeTab === 'notCreated'} onClick={() => setActiveTab('notCreated')}>
          {isArabic ? 'لم يتم إنشاء الشحنة' : 'Shipment Not Created'} ({statusCounts.notCreated})
        </Tab>
        <Tab active={activeTab === 'shippingRequest'} onClick={() => setActiveTab('shippingRequest')}>
          {isArabic ? 'طلب شحن' : 'Shipping Request'} ({statusCounts.shippingRequest})
        </Tab>
        <Tab active={activeTab === 'received'} onClick={() => setActiveTab('received')}>
          {isArabic ? 'تم الاستلام في المتجر' : 'Received in Store'} ({statusCounts.received})
        </Tab>
        <Tab active={activeTab === 'out'} onClick={() => setActiveTab('out')}>
          {isArabic ? 'قيد التوصيل' : 'Out for Delivery'} ({statusCounts.out})
        </Tab>
        <Tab active={activeTab === 'delivered'} onClick={() => setActiveTab('delivered')}>
          {isArabic ? 'تم التسليم' : 'Delivered'} ({statusCounts.delivered})
        </Tab>
        <Tab active={activeTab === 'refunded'} onClick={() => setActiveTab('refunded')}>
          {isArabic ? 'مسترجع' : 'Refunded'} ({statusCounts.refunded})
        </Tab>
      </FilterTabs>

      <Header>
        <Title>
          <FiPackage /> {isArabic ? 'الحالة' : 'Status'}
        </Title>
        <HeaderActions>
          <SearchBar>
            <FiSearch />
            <SearchInput
              type="text"
              placeholder={isArabic ? 'ابحث في الطلبات...' : 'Search orders...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
          <IconButton onClick={fetchOrders}>
            <FiRefreshCw /> {isArabic ? 'تحديث' : 'Refresh'}
          </IconButton>
          <SendAllEmailButton
            onClick={() => setBulkEmailModalOpen(true)}
            disabled={sendingBulkEmails || getBulkEmailTargets().length === 0}
            title={isArabic ? 'إرسال إيميل للحالات المؤهلة فقط (جاري التوصيل/تم التسليم)' : 'Send emails for eligible statuses only (Out for delivery / Delivered)'}
          >
            {isArabic ? 'إرسال إيميل للجميع' : 'Send Email to All'}
          </SendAllEmailButton>
        </HeaderActions>
      </Header>

      <Card>
        <TableContainer>
          <Table>
            <thead>
              <TableRow>
                <TableHeaderCell></TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'رقم الطلب' : 'Order #'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الماركة' : 'Brand'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'العميل' : 'Customer'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'بريد العميل' : 'Customer Email'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الهاتف' : 'Phone'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'العناصر' : 'Items'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'التاريخ' : 'Date'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الإجمالي' : 'Total'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الحالة' : 'Status'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الدفع' : 'Payment'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'التتبع' : 'Track'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الإجراء' : 'Action'}</TableHeaderCell>
              </TableRow>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} style={{ textAlign: 'center', padding: '64px' }}>
                    <EmptyIcon>📦</EmptyIcon>
                    <p>{isArabic ? 'لا توجد طلبات' : 'No orders found'}</p>
                  </TableCell>
                </TableRow>
              ) : (
                displayedOrders.map((order, index) => {
                  const key = `${order.id}-${order.brandId}`;
                  const isExpanded = expandedOrders.has(key);
                  
                  // Check if this is first/last in a shared basket group
                  const prevOrder = displayedOrders[index - 1];
                  const nextOrder = displayedOrders[index + 1];
                  const isFirstInBasket = !prevOrder || prevOrder.basketId !== order.basketId;
                  const isLastInBasket = !nextOrder || nextOrder.basketId !== order.basketId;
                  
                  const RowComponent = order.sharedBasket ? SharedBasketRow : TableRow;

                  // Get brand index within basket (1, 2, 3...)
                  const getBrandIndexInBasket = () => {
                    let count = 1;
                    for (let i = index - 1; i >= 0; i--) {
                      if (displayedOrders[i].basketId === order.basketId) {
                        count++;
                      } else {
                        break;
                      }
                    }
                    return count;
                  };

                  return (
                    <React.Fragment key={key}>
                      {/* Spacer row before shared basket group */}
                      {order.sharedBasket && isFirstInBasket && index > 0 && (
                        <tr>
                          <td colSpan={13} style={{ padding: '8px 0', background: 'transparent', border: 'none' }}></td>
                        </tr>
                      )}
                      <RowComponent 
                        isFirst={isFirstInBasket} 
                        isLast={isLastInBasket}
                      >
                        <TableCell>
                          <ExpandButton onClick={() => toggleExpanded(key)}>
                            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                          </ExpandButton>
                        </TableCell>
                        <TableCell>
                          <div>
                            <strong>{formatOrderReference(order)}</strong>
                            {order.sharedBasket && (
                              <BasketLabel>
                                🛒 Same Cart • {getBrandIndexInBasket()} of {order.basketBrandCount}
                              </BasketLabel>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <strong>{getBrandName(order.brandId)}</strong>
                        </TableCell>
                        <TableCell>
                          {order.shipping_snapshot?.full_name || 'Unknown'}
                        </TableCell>
                        <TableCell>
                          {order.customerEmail ? (
                            <a
                              href={`mailto:${order.customerEmail}`}
                              style={{
                                color: '#5efba9',
                                textDecoration: 'none',
                                fontSize: '13px'
                              }}
                              title="Send email"
                            >
                              {order.customerEmail}
                            </a>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '13px' }}>N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {order.shipping_snapshot?.phone_number || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {order.totalQuantity} item{order.totalQuantity !== 1 ? 's' : ''}
                          {order.order_items.length > 1 && (
                            <ItemsBadge>{order.order_items.length} products</ItemsBadge>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(order.created_at)}</TableCell>
                        <TableCell>{order.totalAmount.toFixed(2)} {order.currency}</TableCell>
                        <TableCell>
                          <StatusTracker 
                            progress={order.statusStep} 
                            isRefunded={order.isRefunded}
                            orderItemId={order.order_items?.[0]?.id || null}
                            allOrderItemIds={order.order_items?.map(item => item.id) || null}
                          />
                        </TableCell>
                        <TableCell>
                          <PaymentStatusBadge status={order.payment_status}>
                            {order.payment_status}
                          </PaymentStatusBadge>
                        </TableCell>
                        <TableCell>
                          {order.tracking_url ? (
                            <TrackLink>
                              <TrackText as="a" href={order.tracking_url} target="_blank" rel="noopener noreferrer">
                                Track <FiExternalLink />
                              </TrackText>
                              <CopyIcon 
                                onClick={() => {
                                  navigator.clipboard.writeText(order.tracking_url);
                                  showToast({ type: 'success', title: isArabic ? 'تم النسخ' : 'Copied', message: isArabic ? 'تم نسخ رابط التتبع' : 'Tracking link copied' });
                                }} 
                                title="Copy URL" 
                              />
                            </TrackLink>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {order.statusStep === 0 && !order.tracking_url ? (
                            <CreateShipmentButton
                              onClick={() => openShipmentModal(order.brandId, order.id, getBrandName(order.brandId), order.shipping_snapshot?.country || null)}
                            >
                              <FiTruck /> Ship
                            </CreateShipmentButton>
                          ) : (order.statusStep === 3 || order.statusStep === 4) && order.customerEmail ? (
                            <SendEmailButton onClick={() => openSendEmailModal(order)}>
                              {isArabic ? 'إرسال إيميل' : 'Send Email'}
                            </SendEmailButton>
                          ) : order.tracking_url ? (
                            <span style={{ color: '#059669', fontSize: '12px', fontWeight: '500' }}>✓ Done</span>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                          )}
                        </TableCell>
                      </RowComponent>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={13}>
                            <ExpandedContent>
                              <ExpandedSection>
                                <SectionTitle>Order Items ({order.order_items?.length || 0})</SectionTitle>
                                {order.order_items && order.order_items.length > 0 ? (
                                  order.order_items.map(item => {
                                    // Extract additional information from attributes (custom fields)
                                    const additionalInfo = (item.attributes && typeof item.attributes === 'object' && Object.keys(item.attributes).length > 0) ? item.attributes : null;
                                    
                                    return (
                                      <div key={item.id}>
                                        <ItemRow>
                                      <ItemDetails>
                                        <ItemImage
                                          src={item.products?.images?.[0] || 'https://via.placeholder.com/48'}
                                          alt={item.product_name}
                                        />
                                        <ItemInfo>
                                          <ItemName>{item.product_name}</ItemName>
                                          <ItemMeta>
                                            SKU: {item.sku || 'N/A'} • Qty: {item.quantity} • {item.unit_price?.toFixed(2) || '0.00'} OMR each
                                          </ItemMeta>
                                          {item.note && (
                                            <ItemMeta style={{ marginTop: '4px', fontStyle: 'italic', color: '#4b5563' }}>
                                              Note: {item.note}
                                            </ItemMeta>
                                          )}
                                              {additionalInfo && (
                                                <div style={{ marginTop: '8px', fontSize: '11px', color: '#374151' }}>
                                                  <div style={{ fontWeight: 600, marginBottom: '4px', color: '#1f2937' }}>
                                                    {isArabic ? 'معلومات إضافية' : 'Additional Information'}:
                                                  </div>
                                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                    {Object.entries(additionalInfo).map(([key, value]) => {
                                                      if (value === null || value === undefined || value === '') return null;
                                                      
                                                      // Get label from custom fields map
                                                      const fieldInfo = customFieldsMap[key];
                                                      const displayLabel = fieldInfo 
                                                        ? (isArabic && fieldInfo.label_ar ? fieldInfo.label_ar : fieldInfo.label)
                                                        : key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                                      
                                                      let displayValue = value;
                                                      if (Array.isArray(value)) {
                                                        displayValue = value.join(', ');
                                                      } else if (typeof value === 'object') {
                                                        displayValue = JSON.stringify(value, null, 2);
                                                      } else {
                                                        displayValue = String(value);
                                                      }
                                                      
                                                      return (
                                                        <div key={key} style={{ display: 'flex', gap: '8px' }}>
                                                          <span style={{ fontWeight: 500, minWidth: '100px' }}>{displayLabel}:</span>
                                                          <span style={{ color: '#6b7280' }}>{displayValue}</span>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              )}
                                        </ItemInfo>
                                      </ItemDetails>
                                      <div style={{ fontWeight: 600 }}>
                                        {item.line_total?.toFixed(2) || '0.00'} OMR
                                      </div>
                                    </ItemRow>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <p style={{ color: '#6b7280', fontSize: '14px' }}>No items in this order</p>
                                )}
                              </ExpandedSection>

                              <ExpandedSection>
                                <SectionTitle>Shipping Information</SectionTitle>
                                {order.shipping_snapshot && Object.keys(order.shipping_snapshot).length > 0 ? (
                                  <div style={{ fontSize: '14px', color: '#374151' }}>
                                    <div><strong>Name:</strong> {order.shipping_snapshot.full_name || 'N/A'}</div>
                                    <div><strong>Phone:</strong> {order.shipping_snapshot.phone_number || 'N/A'}</div>
                                    <div><strong>Email:</strong> {order.shipping_snapshot.email || 'N/A'}</div>
                                    <div><strong>Address:</strong> {order.shipping_snapshot.area}, {order.shipping_snapshot.city}, {order.shipping_snapshot.governorate}</div>
                                    {order.shipping_snapshot.delivery_notes && (
                                      <div style={{ marginTop: '8px', fontStyle: 'italic' }}>Notes: {order.shipping_snapshot.delivery_notes}</div>
                                    )}
                                    <div style={{ marginTop: 12 }}>
                                      <button
                                        type="button"
                                        onClick={() => openEditAddressModal(order.id)}
                                        style={{
                                          padding: '8px 12px',
                                          borderRadius: 10,
                                          border: '1px solid #e5e7eb',
                                          background: 'white',
                                          cursor: 'pointer',
                                          fontSize: 13,
                                          fontWeight: 700
                                        }}
                                      >
                                        {isArabic ? 'تعديل العنوان' : 'Edit address'}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <p style={{ color: '#6b7280', fontSize: '14px' }}>No shipping information available</p>
                                )}
                              </ExpandedSection>

                              {order.customer_note && (
                                <ExpandedSection>
                                  <SectionTitle>Customer Notes</SectionTitle>
                                  <p style={{ color: '#374151', fontSize: '14px' }}>{order.customer_note}</p>
                                </ExpandedSection>
                              )}

                              {order.admin_note && (
                                <ExpandedSection>
                                  <SectionTitle>Admin Notes</SectionTitle>
                                  <p style={{ color: '#374151', fontSize: '14px' }}>{order.admin_note}</p>
                                </ExpandedSection>
                              )}
                            </ExpandedContent>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        </TableContainer>
      </Card>

      {!loading && hasMore && (
        <LoadMoreButton onClick={() => setDisplayedCount(prev => prev + 20)}>
          Load More ({filteredOrders.length - displayedCount} remaining)
        </LoadMoreButton>
      )}

      {/* Create Shipment Modal */}
      {shipmentModal.open && (
        <ModalOverlay onClick={closeShipmentModal}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>Create Shipment for {shipmentModal.brandName}</ModalTitle>
            <ModalText>
              This will create a <strong>separate shipment</strong> for <strong>{shipmentModal.brandName}</strong>'s items in this order.
              <br /><br />
              <span style={{ fontSize: '13px', color: '#7c3aed' }}>
                💡 Note: If this basket has multiple brands, each brand needs its own shipment created separately.
              </span>
              <br /><br />
              The shipment will be sent to WASL delivery system for processing.
            </ModalText>
            {shipmentModal.country && String(shipmentModal.country).toLowerCase() !== 'oman' && (
              <div style={{
                marginTop: 12,
                marginBottom: 10,
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid #fde68a',
                background: '#fffbeb',
                color: '#92400e',
                fontSize: 13
              }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>International order</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={shipmentModal.forceOman}
                    onChange={(e) => setShipmentModal((prev) => ({ ...prev, forceOman: e.target.checked }))}
                  />
                  <span>Create shipment as Oman</span>
                </label>
                <div style={{ marginTop: 6, opacity: 0.9 }}>
                  Current country: {shipmentModal.country}
                </div>
              </div>
            )}
            <ModalActions>
              <ModalButton onClick={closeShipmentModal} disabled={creatingShipment}>
                Cancel
              </ModalButton>
              <ModalButton primary onClick={createShipment} disabled={creatingShipment}>
                {creatingShipment ? (
                  <>Creating... <Spinner /></>
                ) : (
                  `Create ${shipmentModal.brandName} Shipment`
                )}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {addressModal.open && (
        <ModalOverlay onClick={closeEditAddressModal}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: '92%', textAlign: 'left' }}>
            <ModalTitle>{isArabic ? 'تعديل عنوان الطلب' : 'Edit order address'}</ModalTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{isArabic ? 'الاسم' : 'Full name'}</div>
                <input
                  value={addressForm.full_name}
                  onChange={(e) => setAddressForm((p) => ({ ...p, full_name: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{isArabic ? 'رقم الهاتف' : 'Phone'}</div>
                <input
                  value={addressForm.phone_number}
                  onChange={(e) => setAddressForm((p) => ({ ...p, phone_number: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{isArabic ? 'البريد الإلكتروني' : 'Email'}</div>
                <input
                  value={addressForm.email}
                  onChange={(e) => setAddressForm((p) => ({ ...p, email: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{isArabic ? 'الدولة' : 'Country'}</div>
                <select
                  value={addressForm.country}
                  onChange={(e) => setAddressForm((p) => ({ ...p, country: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                >
                  <option value="">{isArabic ? 'اختر الدولة' : 'Select country'}</option>
                  {countryOptions.map((countryName) => (
                    <option key={countryName} value={countryName}>
                      {countryName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{isArabic ? 'المحافظة' : 'Governorate'}</div>
                <input
                  value={addressForm.governorate}
                  onChange={(e) => setAddressForm((p) => ({ ...p, governorate: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{isArabic ? 'المدينة' : 'City'}</div>
                <input
                  value={addressForm.city}
                  onChange={(e) => setAddressForm((p) => ({ ...p, city: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{isArabic ? 'المنطقة' : 'Area'}</div>
                <input
                  value={addressForm.area}
                  onChange={(e) => setAddressForm((p) => ({ ...p, area: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{isArabic ? 'ملاحظات التوصيل' : 'Delivery notes'}</div>
                <textarea
                  value={addressForm.delivery_notes}
                  onChange={(e) => setAddressForm((p) => ({ ...p, delivery_notes: e.target.value }))}
                  rows={3}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', resize: 'vertical' }}
                />
              </div>
            </div>

            <ModalActions style={{ justifyContent: 'flex-end' }}>
              <ModalButton onClick={closeEditAddressModal} disabled={savingAddress}>
                {isArabic ? 'إلغاء' : 'Cancel'}
              </ModalButton>
              <ModalButton primary onClick={saveOrderAddress} disabled={savingAddress}>
                {savingAddress ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : (isArabic ? 'حفظ' : 'Save')}
                {savingAddress && <Spinner />}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {emailModal.open && (
        <ModalOverlay onClick={closeSendEmailModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{isArabic ? 'تأكيد إرسال الإيميل' : 'Confirm sending email'}</ModalTitle>
            <ModalText>
              {isArabic
                ? `سيتم إرسال إيميل حالة الشحنة: ${emailModal.statusName}`
                : `This will send shipment status email: ${emailModal.statusName}`}
              {emailModal.customerEmail ? (
                <>
                  <br /><br />
                  <strong>{isArabic ? 'المستلم:' : 'Recipient:'}</strong> {emailModal.customerEmail}
                </>
              ) : null}
            </ModalText>
            <ModalActions>
              <ModalButton onClick={closeSendEmailModal} disabled={sendingStatusEmail}>
                {isArabic ? 'إلغاء' : 'Cancel'}
              </ModalButton>
              <ModalButton primary onClick={confirmSendStatusEmail} disabled={sendingStatusEmail}>
                {sendingStatusEmail ? (isArabic ? 'جاري الإرسال...' : 'Sending...') : (isArabic ? 'تأكيد الإرسال' : 'Confirm send')}
                {sendingStatusEmail && <Spinner />}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {bulkEmailModalOpen && (
        <ModalOverlay onClick={() => !sendingBulkEmails && setBulkEmailModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{isArabic ? 'تأكيد الإرسال الجماعي' : 'Confirm bulk email send'}</ModalTitle>
            <ModalText>
              {isArabic
                ? `سيتم إرسال إيميل للحالات المؤهلة فقط (جاري التوصيل / تم التسليم). العدد: ${getBulkEmailTargets().length}`
                : `This will send emails for eligible statuses only (Out for Delivery / Delivered). Count: ${getBulkEmailTargets().length}`}
            </ModalText>
            <ModalActions>
              <ModalButton onClick={() => !sendingBulkEmails && setBulkEmailModalOpen(false)} disabled={sendingBulkEmails}>
                {isArabic ? 'إلغاء' : 'Cancel'}
              </ModalButton>
              <ModalButton primary onClick={confirmSendBulkEmails} disabled={sendingBulkEmails}>
                {sendingBulkEmails ? (isArabic ? 'جاري الإرسال...' : 'Sending...') : (isArabic ? 'تأكيد الإرسال' : 'Confirm send')}
                {sendingBulkEmails && <Spinner />}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default SuperAdminStatus;
